import {
  extractIdLike,
  extractOptionValue,
} from '@/domain/product/persistence/propertyRules'
import { ReviewStatus } from '@skop-app/lib-types'
import type { GridContext } from '@/types/grid'
import { getRefDataByKeyStore } from '@/components/Grid/columns/refDataStore'
import { z } from 'zod/v4'

type RefDisplayParams = {
  field: string
  fieldType?: string
  context?: GridContext
  data?: Record<string, unknown>
  refData?: Record<string, string>
  refDataByKeyStoreId?: string
  value: unknown
}

export type RefDisplayEntry = {
  key: string
  label: string
  status?: string
  isPending: boolean
}

export const pendingRefDisplayClassName =
  'underline decoration-amber-500 decoration-dashed underline-offset-2'

const KNOWN_DYNAMIC_FIELDS = new Set([
  'byUserID',
  'organizationID',
  'projectID',
  'placeID',
])

const normalizePrimitive = (value: unknown): string | null => {
  if (value == null) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

const normalizeDisplayToken = (value: unknown): string | null => {
  const direct = normalizePrimitive(value)
  if (direct) return direct

  const idLike = extractIdLike(value)
  if (idLike) return idLike

  const extracted = normalizePrimitive(extractOptionValue(value))
  if (extracted) return extracted

  if (value && typeof value === 'object' && 'label' in value) {
    return normalizePrimitive((value as { label?: unknown }).label)
  }

  return null
}

const normalizeTopLevelFallbackToken = (value: unknown): string | null => {
  const direct = normalizePrimitive(value)
  if (direct) return direct

  if (value && typeof value === 'object' && 'label' in value) {
    const label = normalizePrimitive((value as { label?: unknown }).label)
    if (label) return label
  }

  const extracted = normalizePrimitive(extractOptionValue(value))
  if (extracted) return extracted

  const idLike = extractIdLike(value)
  if (idLike) return idLike

  return null
}

const normalizeDisplayValues = (
  value: unknown,
  normalizeToken: (value: unknown) => string | null = normalizeDisplayToken,
): string[] => {
  const values = Array.isArray(value) ? value : [value]
  return values
    .map((item) => normalizeToken(item))
    .filter((item): item is string => item !== null)
}

const getNestedValue = (path: string, obj: unknown): unknown => {
  if (!obj) return undefined
  const parts = path.split('.')
  let current: unknown = obj

  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }

  return current
}

const readDirectTopLevelFieldValue = (
  row: Record<string, unknown> | undefined,
  key: string,
): unknown => {
  if (!row) return null
  return getNestedValue(key, row)
}

const getRefLookupLabel = ({
  context,
  field,
  refData,
  refDataByKeyStoreId,
  key,
}: Omit<RefDisplayParams, 'data' | 'value'> & {
  key: string
}): string | undefined => {
  const storeLookup = getRefDataByKeyStore(refDataByKeyStoreId)?.[field]
  const contextLookup = context?.refDataByKey?.[field]

  // Preserve spread precedence without copying every reference for each cell.
  if (storeLookup && Object.hasOwn(storeLookup, key)) return storeLookup[key]
  if (contextLookup && Object.hasOwn(contextLookup, key))
    return contextLookup[key]
  return refData && Object.hasOwn(refData, key) ? refData[key] : undefined
}

const getOptionLookupLabel = ({
  context,
  field,
  optionId,
}: {
  context?: GridContext
  field: string
  optionId: string
}): string | undefined => {
  return context?.optionsByID?.[field]?.[optionId]?.label
}

const getOptionLookupStatus = ({
  context,
  field,
  optionId,
}: {
  context?: GridContext
  field: string
  optionId: string
}): string | undefined => {
  return context?.optionsByID?.[field]?.[optionId]?.status
}

const getUnresolvedDynamicLabel = ({
  context,
  field,
  fieldType,
}: Pick<RefDisplayParams, 'context' | 'field' | 'fieldType'>):
  | string
  | undefined => {
  if (fieldType !== 'DYNAMIC' && !KNOWN_DYNAMIC_FIELDS.has(field)) {
    return undefined
  }

  return (
    context?.dynamicFallbackLabels?.[field] ??
    context?.dynamicFallbackLabels?.default
  )
}

const getUnresolvedListLabel = ({
  context,
  fieldType,
  key,
}: Pick<RefDisplayEntry, 'key'> &
  Pick<RefDisplayParams, 'context' | 'fieldType'>): string | undefined => {
  if (fieldType !== 'LIST') return undefined
  if (!z.uuid().safeParse(key).success) return undefined

  return context?.dynamicFallbackLabels?.default
}

export const resolveRefDisplayEntries = ({
  context,
  data,
  field,
  fieldType,
  refData,
  refDataByKeyStoreId,
  value,
}: RefDisplayParams): RefDisplayEntry[] => {
  const normalizedValues = normalizeDisplayValues(value)
  if (normalizedValues.length === 0) return []

  const unresolvedDynamicLabel = getUnresolvedDynamicLabel({
    context,
    field,
    fieldType,
  })
  const valueFallbackLabels = normalizeDisplayValues(
    value,
    normalizeTopLevelFallbackToken,
  )
  const topLevelLabels = normalizeDisplayValues(
    readDirectTopLevelFieldValue(data, field),
    normalizeTopLevelFallbackToken,
  )

  return normalizedValues.flatMap((item, index) => {
    if (context?.deletedOptionIdsByKey?.[field]?.includes(item)) return []
    const lookupLabel =
      getRefLookupLabel({
        context,
        field,
        refData,
        refDataByKeyStoreId,
        key: item,
      }) ??
      getOptionLookupLabel({
        context,
        field,
        optionId: item,
      })
    const topLevelLabel =
      topLevelLabels[index] ??
      (normalizedValues.length === 1 ? topLevelLabels[0] : undefined)
    const resolvedTopLevelLabel =
      topLevelLabel === item ? undefined : topLevelLabel
    const valueFallbackLabel =
      valueFallbackLabels[index] ??
      (normalizedValues.length === 1 ? valueFallbackLabels[0] : undefined)
    const resolvedValueFallbackLabel =
      valueFallbackLabel === item ? undefined : valueFallbackLabel
    const status = getOptionLookupStatus({
      context,
      field,
      optionId: item,
    })
    const unresolvedListLabel = getUnresolvedListLabel({
      context,
      fieldType,
      key: item,
    })

    return [
      {
        key: item,
        label:
          lookupLabel ??
          resolvedTopLevelLabel ??
          resolvedValueFallbackLabel ??
          unresolvedListLabel ??
          unresolvedDynamicLabel ??
          item,
        status,
        isPending: status === ReviewStatus.PENDING,
      },
    ]
  })
}

export const formatRefDisplayValue = (params: RefDisplayParams): string => {
  return resolveRefDisplayEntries(params)
    .map((entry) => entry.label)
    .join(', ')
}

export const hasPendingRefDisplayValue = (
  params: Omit<RefDisplayParams, 'data' | 'refData' | 'refDataByKeyStoreId'>,
): boolean => {
  return resolveRefDisplayEntries({
    ...params,
    data: undefined,
    refData: undefined,
    refDataByKeyStoreId: undefined,
  }).some((entry) => entry.isPending)
}
